import React, { useState, useEffect } from 'react';
import { Loader2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GalleryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setItems(response.data.items || []);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const categories = ['all', ...new Set(items.map(item => item.category).filter(Boolean))];
  
  const filteredItems = selectedCategory === 'all' 
    ? items 
    : items.filter(item => item.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#C9A96E] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Hero */}
      <section className="relative py-24 bg-[#2C1810]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Portfolio</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-4 mb-6">
            Notre Galerie
          </h1>
          <div className="w-20 h-1 bg-gradient-to-r from-[#C9A96E] to-[#A67B5B] mx-auto mb-6" />
          <p className="text-[#D4C4B5] text-lg max-w-2xl mx-auto">
            Découvrez nos réalisations et laissez-vous inspirer par les transformations de nos clientes.
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Category Filter */}
          <div className="flex justify-center gap-4 mb-12 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-[#C9A96E] text-white'
                    : 'bg-white text-[#2C1810] hover:bg-[#F5F0EB]'
                }`}
              >
                {cat === 'all' ? 'Tout voir' : cat}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon size={48} className="mx-auto text-[#D4C4B5] mb-4" />
              <p className="text-[#5D4037]">Aucune image disponible pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.map((item) => (
                <div key={item.id} className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all">
                  <div className="aspect-square bg-[#F5F0EB] relative overflow-hidden">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl.startsWith('/uploads') ? `${BACKEND_URL}${item.imageUrl}` : item.imageUrl}
                        alt={item.title || 'Gallery image'}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={48} className="text-[#D4C4B5]" />
                      </div>
                    )}
                  </div>
                  {(item.title || item.description) && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform">
                      {item.title && <h3 className="font-semibold text-lg mb-1">{item.title}</h3>}
                      {item.description && <p className="text-sm text-gray-200">{item.description}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
